import { useMemo, useRef, useState } from "react";
import { InstaPassOrb } from "./InstaPassOrb";
import { HaileyChat } from "./HaileyChat";

/* ══════════════════════════════════════════════════════════════════════════
   HaileyHero — orb + chat, composed.

   This is the piece that makes the orb appear. HaileyChat on its own is just
   the conversation panel; the orb lives here, and the two are joined by three
   wires that are easy to miss:

     onOrbState  — the chat pushes its state up (idle → thinking → speaking →
                   listening) so the orb animates in time with her.
     onAmp       — while she speaks, the chat pushes the live waveform level,
                   which drives the orb's glow, rings and scale frame by frame.
     chatRef     — tapping the orb calls back into the chat's onOrbTap(),
                   which focuses the composer and speaks the greeting once.

   Drop any of them and you get a pretty orb that does nothing, or a chat with
   no orb — which is exactly what the site showed before this file existed.

   ── Layouts ─────────────────────────────────────────────────────────────
   "split"  (default) — orb large on the left, chat on the right. This is the
                        homepage hero: the orb is the thing you see first, and
                        it is big enough to be the page's focal point.
   "stack"            — orb above chat, centred. For narrow containers: the
                        floating panel, and every layout below the lg breakpoint
                        (the split grid collapses to this automatically).
   ══════════════════════════════════════════════════════════════════════════ */

function SparkLayer() {
  const sparks = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => {
        const dur = 2.4 + ((i * 17) % 32) / 10;
        const sc = 0.6 + ((i * 13) % 18) / 10;
        return {
          left: `${(i * 37) % 100}%`,
          top: `${(i * 53) % 100}%`,
          duration: `${dur}s`,
          delay: `${(i * 0.21) % dur}s`,
          sop: (0.4 + ((i * 11) % 50) / 100).toFixed(2),
          size: `${2 * sc}px`,
        };
      }),
    []
  );

  return (
    <div className="sparks" aria-hidden="true">
      {sparks.map((s, i) => (
        <span
          key={i}
          className="spark"
          style={{
            left: s.left,
            top: s.top,
            animationDuration: s.duration,
            animationDelay: s.delay,
            "--sop": s.sop,
            width: s.size,
            height: s.size,
          }}
        />
      ))}
    </div>
  );
}

/** Hailey: glowing InstaPass orb + chat window. */
export function HaileyHero({ layout = "split" }) {
  const chatRef = useRef(null);
  const [orbState, setOrbState] = useState("idle");
  const [amp, setAmp] = useState(0);
  const stacked = layout === "stack";

  return (
    <div className={`hailey-hero hailey-hero--${stacked ? "stack" : "split"}`}>
      <SparkLayer />

      <div className="ip-hero-lockup">
        <div className="hero-emma-center pai-hero">
          <InstaPassOrb
            state={orbState}
            amp={amp}
            onTap={() => chatRef.current?.onOrbTap()}
          />
        </div>
        {!stacked && (
          <>
            {/* h2, not h1: the page's own headline above this band is the h1.
                Styling is by class, so the tag change is invisible. */}
            <h2 className="ip-name">
              INSTAPASS <span className="ip-name-ai">AI</span>
            </h2>
            <p className="ip-role">AI Ticket Concierge</p>
            <p className="ip-tag">
              Live ticket concierge — real inventory, real availability.
            </p>
          </>
        )}
        <p className="ip-hero-hint">
          Tap the orb — or type to search live inventory
        </p>
      </div>

      <HaileyChat ref={chatRef} onOrbState={setOrbState} onAmp={setAmp} />
    </div>
  );
}

export default HaileyHero;
